import React, { useEffect, useRef, useState } from "react"
import { Box, Dialog, DialogContent, styled, Typography, IconButton, Grid } from "@mui/material"
import AppButton from "components/StyledComponents/AppButton"
import CloseIcon from "@mui/icons-material/Close"
import RefreshIcon from "@mui/icons-material/Refresh"
import { useTheme } from "@mui/system"
import { useDispatch, useSelector } from "react-redux"
import { marketingActions } from "redux/marketing/slice/user"

import BasicDetailsForm from "./BasicDetailsForm"
import DidYouKnowForm from "./DidYouKnowForm"
import HistoryForm from "./HistoryForm"
import MobilePreview from "components/MobilePreview"

const buttonText = {
  EDIT: "Update Item",
  SAVE: "Save Item"
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    backgroundColor: "transparent",
    maxWidth: 1400,
    width: "100%"
  },
  "& .MuiDialogContent-root": {
    backgroundColor: theme.palette.background.default,
    borderRadius: 12,
    border: "none",
    width: "100%",
    padding: 35,
    boxSizing: "border-box"
  }
}))

function MenuItemsForm({
  formik,
  open = false,
  onClose = () => {},
  isEdit = false,
  menuFields,
  title,
  saveBeforeAi = () => {}
}) {
  const theme = useTheme()
  const childRef = useRef()
  const dispatch = useDispatch()
  const { soundbites } = useSelector((state) => state.marketing || {})
  const { linkCategory } = useSelector((state) => state.marketing?.menu || {})
  // Bumped by the Refresh Preview button to force the preview to re-read the
  // latest form values (the preview also updates live; this is a manual nudge).
  const [previewKey, setPreviewKey] = useState(0)

  const handleClose = () => {
    if (childRef.current?.stopAudio) {
      childRef.current.stopAudio()
    }
    dispatch(marketingActions.setSoundbites({}))
    onClose()
  }

  const handleSubmit = () => {
    if (childRef.current?.stopAudio) {
      childRef.current.stopAudio()
    }
    formik.handleSubmit()
  }

  useEffect(() => {
    formik.resetForm({
      dishName: "",
      image: "",
      menuDescription: [],
      allergens: [],
      chefDescription: "",
      itemPrice: null,
      notification_date: "",
      notification_time: ""
    })
    dispatch(marketingActions.setMenuImageId(""))
    dispatch(marketingActions.resetDidYouKnowImage())
    dispatch(marketingActions.resetHistoryMedia())
    dispatch(marketingActions.setSoundbites({}))
  }, [open])

  const styles = {
    closeBtn: {
      color: theme.palette.text.primary,
      position: "absolute",
      top: "10px",
      right: "10px",
      backgroundColor: theme.palette.background.main
    }
  }

  useEffect(() => {
    dispatch(marketingActions.setDidYouKnowData([]))
    dispatch(marketingActions.setHistoryData([]))
  }, [open])

  const dataToDisplay = () => {
    const values = formik.values
    const data_to_display = menuFields
      ?.filter(
        (field) =>
          field.label !== "notification_time" &&
          field.label !== "notification_date" &&
          field.label !== "price" &&
          field.label !== "name"
      )
      ?.map((field) => {
        const val = values[field.label]
        if (!val && val !== 0) return null

        return {
          filedName: field.fieldName,
          value: val,
          show_bullets:
            field.label === "ingredients" ||
            field.label === "glossary" ||
            field.label === "unique_facts"
        }
      })
      .filter(Boolean)

    return {
      name: values.name,
      price: values.price,
      menu_description: values.menu_description,
      image:
        values.image instanceof File
          ? values.image.preview || URL.createObjectURL(values.image)
          : values.image?.url || values.image || "",
      soundbite_url: soundbites?.url || values.soundbite_url,
      did_you_know: values.did_you_know?.map((item) => ({
        title: item.heading,
        description: item.content,
        image: {
          url:
            item.image instanceof File
              ? item.image.preview || URL.createObjectURL(item.image)
              : item.image?.url || item.image || ""
        }
      })),
      item_history: {
        title: values.item_history?.heading,
        description: values.item_history?.content
      },
      data_to_display,
      meal_period: { name: title },
      linkCategory,
      bar_category: linkCategory?.item === "bar menu",
      wine_category: linkCategory?.item?.toLowerCase().includes("wine"),
      country: values.country,
      spirit_category: { name: linkCategory?.item === "spirits" ? "Spirits" : "" }
    }
  }

  return (
    <StyledDialog open={open}>
      <DialogContent>
        <Box className="d-flex justify-content-end">
          <IconButton onClick={() => handleClose()} sx={styles.closeBtn}>
            <CloseIcon sx={{ height: "14px", width: "14px" }} />
          </IconButton>
        </Box>
        <Grid container spacing={4}>
          <Grid item xs={8}>
            <Box className="w-100 d-flex justify-content-center mb-2">
              <Typography variant="h2">
                {isEdit ? "Edit" : "Add New"} {title}
              </Typography>
            </Box>
            <BasicDetailsForm
              ref={childRef}
              menuFields={menuFields}
              formik={formik}
              isEdit={isEdit}
              saveBeforeAi={saveBeforeAi}
            />
            <DidYouKnowForm
              formik={formik}
              isEdit={isEdit}
              values={formik.values?.["did_you_know"]}
              name={"did_you_know"}
              buttonText={"Add Did you know"}
              saveBeforeAi={saveBeforeAi}
            />
            <HistoryForm formik={formik} isEdit={isEdit} saveBeforeAi={saveBeforeAi} />
            <Box className="mt-5 d-flex justify-content-around">
              <AppButton
                onClick={handleSubmit}
                sx={() => ({
                  height: "48px",
                  padding: "10px 50px"
                })}>
                {isEdit ? buttonText.EDIT : buttonText.SAVE}
              </AppButton>
            </Box>
          </Grid>
          <Grid
            item
            xs={4}
            className="d-none d-md-block sticky-top "
            sx={{
              height: "fit-content"
            }}>
            <Box className="d-flex justify-content-end mb-2">
              <AppButton
                styles={{ padding: "8px 16px", fontSize: "13px" }}
                onClick={() => setPreviewKey((k) => k + 1)}>
                <RefreshIcon sx={{ fontSize: 18, mr: 0.7 }} />
                Refresh Preview
              </AppButton>
            </Box>
            <MobilePreview key={previewKey} inline={true} data={dataToDisplay()} />
          </Grid>
        </Grid>
      </DialogContent>
    </StyledDialog>
  )
}

export default MenuItemsForm
